import PropTypes from 'prop-types';
import STEP2_COPY from './copy';
import { fmt } from './formatters';

export function DetailChargesCAE({ resultat }) {
  if (!resultat?.details) return null;
  const d = resultat.details;
  const ligne = (label, valeur) => (
    <div key={label} className="flex justify-between text-xs">
      <span className="text-green-700">{label}</span>
      <span className="font-medium text-green-900 tabular-nums">{fmt(valeur)}/mois</span>
    </div>
  );

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
      <p className="text-sm font-semibold text-green-800 mb-3">{STEP2_COPY.details.caeParticipationTitle}</p>
      <div className="space-y-1.5">
        {(resultat.contribution_cae ?? 0) > 0 && ligne('Contribution CAE (barème dégressif) :', resultat.contribution_cae)}
        {(d.rcp_cgscop_mensuel ?? 0) > 0 && ligne(d.cgscop_inclu ? 'RCP ou Décennale :' : 'RCP ou Décennale + CGscop :', d.rcp_cgscop_mensuel)}
        {d.label_bio_cae > 0 && ligne('Label bio :', d.label_bio_cae)}
        {d.logiciel_formation_cae > 0 && ligne('Logiciel formation :', d.logiciel_formation_cae)}
        <div className="flex justify-between text-xs font-semibold text-green-800 border-t border-green-200 pt-1.5 mt-0.5">
          <span>Total :</span>
          <span className="tabular-nums">{fmt(resultat.categories_charges?.frais_participation_coop ?? 0)}/mois</span>
        </div>
      </div>

      <p className="text-sm font-semibold text-green-800 mb-3 mt-8">{STEP2_COPY.details.caeNetTitle}</p>
      <div className="space-y-1.5">
        {(d.charges_forfaitaires ?? 0) > 0 && ligne('Mutuelle + médecine du travail :', d.charges_forfaitaires)}
        {(d.cotisation_fede_urscop ?? 0) > 0 && ligne('Cotisations Fédération CAE + URSCOP :', d.cotisation_fede_urscop)}
        <div className="flex justify-between text-xs font-semibold text-green-800 border-t border-green-200 pt-1.5 mt-0.5">
          <span>Total :</span>
          <span className="tabular-nums">{fmt((d.charges_forfaitaires ?? 0) + (d.cotisation_fede_urscop ?? 0))}/mois</span>
        </div>
      </div>
      <p className="text-xxs text-green-700 mt-4">{STEP2_COPY.details.unitReminder}</p>
    </div>
  );
}

DetailChargesCAE.propTypes = {
  resultat: PropTypes.shape({
    details: PropTypes.shape({
      rcp_cgscop_mensuel: PropTypes.number,
      label_bio_cae: PropTypes.number,
      logiciel_formation_cae: PropTypes.number,
      charges_forfaitaires: PropTypes.number,
      cotisation_fede_urscop: PropTypes.number,
    }),
    categories_charges: PropTypes.shape({
      frais_participation_coop: PropTypes.number,
    }),
    contribution_cae: PropTypes.number,
  }),
};

const MIN_STRUCTURE_LIGNES = [
  { id: 'logiciel_formation', label: 'Logiciel de formation', min: 200, statuts: ['micro', 'ei', 'sasu'], formation: true },
  { id: 'label_bio', label: 'Label bio', min: Math.round((160 + 180) / 12), statuts: ['micro', 'ei', 'sasu'], alimentaire: true },
  { id: 'logiciel_compta', label: 'Logiciel de comptabilité', min: 20, statuts: ['micro', 'ei', 'sasu'] },
  { id: 'compte_bancaire', label: 'Compte bancaire', min: 10, statuts: ['micro', 'ei', 'sasu'] },
  { id: 'assurance_rcp', label: 'Assurance RCP', min: 15, statuts: ['micro', 'ei', 'sasu'], hors_batiment: true },
  { id: 'assurance_decennale', label: 'Assurance décennale', min: 250, statuts: ['micro', 'ei', 'sasu'], batiment: true },
  { id: 'comptable', label: 'Comptable', min: 250, statuts: ['ei', 'sasu'] },
  { id: 'cet_cvae_cfe', label: 'CET / CFE', min: 27, statuts: ['micro', 'ei', 'sasu'] },
  { id: 'mutuelle_prevoyance', label: 'Mutuelle / prévoyance', min: 75, statuts: ['micro', 'ei', 'sasu'] },
];

export function DetailAutresCharges({ resultat, metier, statut, charges }) {
  if (!resultat || resultat.incompatible) return null;
  const isBatiment = metier === 'Bâtiment, travaux';
  const isFormation = metier === 'Formation professionnelle';
  const isAlimentaire = metier === 'Production alimentaire, culinaire';
  const total = resultat.categories_charges?.autres_frais_structure ?? 0;
  const lignes = MIN_STRUCTURE_LIGNES.filter(({ statuts, batiment, hors_batiment, formation, alimentaire }) => {
    if (!statuts.includes(statut)) return false;
    if (batiment && !isBatiment) return false;
    if (hors_batiment && isBatiment) return false;
    if (formation && !isFormation) return false;
    if (alimentaire && !isAlimentaire) return false;
    return true;
  });

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <p className="text-sm font-semibold text-gray-700 mb-1">{STEP2_COPY.details.autresChargesTitle}</p>
      <p className="text-xs text-gray-500 mb-3">{STEP2_COPY.details.autresChargesSubtitle}</p>
      <div className="space-y-1.5">
        {lignes.map(({ id, label, min }) => {
          const saisie = charges?.[id] || 0;
          const retenu = Math.max(saisie, min);
          if (retenu <= 0) return null;
          return (
            <div key={id} className="flex justify-between text-xs">
              <span className="text-gray-600">{label} :</span>
              <span className={`tabular-nums ${'text-gray-700 font-medium'}`}>
                {fmt(retenu)}/mois
              </span>
            </div>
          );
        })}
        <div className="flex justify-between text-xs font-semibold text-gray-700 border-t border-gray-200 pt-1.5 mt-0.5">
          <span>Total appliqué :</span>
          <span className="tabular-nums">{fmt(total)}/mois</span>
        </div>
      </div>
      <p className="text-xxs text-gray-500 mt-3">{STEP2_COPY.details.unitReminder}</p>
    </div>
  );
}

DetailAutresCharges.propTypes = {
  resultat: PropTypes.shape({
    incompatible: PropTypes.bool,
    categories_charges: PropTypes.shape({
      autres_frais_structure: PropTypes.number,
    }),
  }),
  metier: PropTypes.string.isRequired,
  statut: PropTypes.oneOf(['micro', 'ei', 'sasu']).isRequired,
  charges: PropTypes.object,
};
